import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Helper function to decode JWT token
const decodeJWT = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Error decoding JWT:', error);
        return null;
    }
};

// Create axios instance for customer API
const customerApi = axios.create();

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Refresh token function
    const refreshToken = async () => {
        const refreshTokenValue = localStorage.getItem('customer_refresh_token');
        
        if (!refreshTokenValue) {
            throw new Error('No refresh token available');
        }

        try {
            const response = await axios.post(
                getApiUrl('api/public/customer/refresh'),
                {
                    refresh_token: refreshTokenValue
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }
            );

            if (response.data.success && response.data.data) {
                const { access_token, refresh_token, token_type, expires_in } = response.data.data;
                
                // Decode new access token to extract user information
                const decodedToken = decodeJWT(access_token);
                
                if (!decodedToken) {
                    throw new Error('Failed to decode refreshed token');
                }

                // Get current user data for fallback
                const currentUserData = user || JSON.parse(localStorage.getItem('customer_user') || '{}');

                // Update user data from new token
                const userData = {
                    id: decodedToken.customer_id || decodedToken.sub,
                    name: decodedToken.name || currentUserData?.name || '',
                    username: decodedToken.username || currentUserData?.username || '',
                    email: decodedToken.email || currentUserData?.email || '',
                    mobile_number: decodedToken.mobile_number || currentUserData?.mobile_number || '',
                    state: decodedToken.state || currentUserData?.state || ''
                };

                // Update stored tokens and user data
                localStorage.setItem('customer_access_token', access_token);
                localStorage.setItem('customer_refresh_token', refresh_token);
                localStorage.setItem('customer_token_type', token_type);
                localStorage.setItem('customer_token_expires_in', expires_in.toString());
                localStorage.setItem('customer_user', JSON.stringify(userData));
                
                setUser(userData);
                
                return { access_token, refresh_token };
            } else {
                throw new Error(response.data.message || 'Token refresh failed');
            }
        } catch (error) {
            // If refresh fails, logout user
            logout();
            throw error;
        }
    };

    // Check if token is expired or about to expire (within 5 minutes)
    const isTokenExpired = () => {
        const accessToken = localStorage.getItem('customer_access_token');
        if (!accessToken) return true;

        const decoded = decodeJWT(accessToken);
        if (!decoded || !decoded.exp) return true;

        // Check if token expires within 5 minutes (300 seconds)
        const expirationTime = decoded.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds

        return (expirationTime - currentTime) < bufferTime;
    };

    // Set up axios interceptor for automatic token refresh
    // Set up once on mount, after all functions are defined
    useEffect(() => {
        // Request interceptor - Add access token to requests
        const requestInterceptor = customerApi.interceptors.request.use(
            async (config) => {
                // Ensure headers object exists
                if (!config.headers) {
                    config.headers = {};
                }
                
                const accessToken = localStorage.getItem('customer_access_token');
                
                // Always add token if available
                if (accessToken) {
                    // Check if token is expired or about to expire
                    if (isTokenExpired()) {
                        if (!isRefreshing) {
                            isRefreshing = true;
                            try {
                                const { access_token } = await refreshToken();
                                config.headers.Authorization = `Bearer ${access_token}`;
                                processQueue(null, access_token);
                                isRefreshing = false;
                            } catch (error) {
                                processQueue(error, null);
                                isRefreshing = false;
                                return Promise.reject(error);
                            }
                        } else {
                            // Wait for ongoing refresh
                            return new Promise((resolve, reject) => {
                                failedQueue.push({ resolve, reject });
                            }).then(token => {
                                config.headers.Authorization = `Bearer ${token}`;
                                return config;
                            }).catch(err => {
                                return Promise.reject(err);
                            });
                        }
                    } else {
                        // Token is valid, add it directly
                        config.headers.Authorization = `Bearer ${accessToken}`;
                    }
                }
                
                // Ensure Content-Type and Accept headers are set
                if (!config.headers['Content-Type']) {
                    config.headers['Content-Type'] = 'application/json';
                }
                if (!config.headers['Accept']) {
                    config.headers['Accept'] = 'application/json';
                }
                
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor - Handle 401 errors and refresh token
        const responseInterceptor = customerApi.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // If error is 401 and we haven't retried yet
                if (error.response?.status === 401 && !originalRequest._retry) {
                    if (isRefreshing) {
                        // Wait for ongoing refresh
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject });
                        }).then(token => {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            return customerApi(originalRequest);
                        }).catch(err => {
                            return Promise.reject(err);
                        });
                    }

                    originalRequest._retry = true;
                    isRefreshing = true;

                    try {
                        const { access_token } = await refreshToken();
                        originalRequest.headers.Authorization = `Bearer ${access_token}`;
                        processQueue(null, access_token);
                        isRefreshing = false;
                        return customerApi(originalRequest);
                    } catch (refreshError) {
                        processQueue(refreshError, null);
                        isRefreshing = false;
                        logout();
                        return Promise.reject(refreshError);
                    }
                }

                return Promise.reject(error);
            }
        );

        // Cleanup interceptors on unmount
        return () => {
            customerApi.interceptors.request.eject(requestInterceptor);
            customerApi.interceptors.response.eject(responseInterceptor);
        };
    }, []); // Empty dependency array - set up once on mount

    // Check if user is logged in on mount
    useEffect(() => {
        const checkAuth = () => {
            const userData = localStorage.getItem('customer_user');
            const accessToken = localStorage.getItem('customer_access_token');
            
            if (userData && accessToken) {
                try {
                    // Check if token is expired
                    if (isTokenExpired()) {
                        // Try to refresh token
                        refreshToken()
                            .then(() => {
                                const refreshedUserData = localStorage.getItem('customer_user');
                                if (refreshedUserData) {
                                    setUser(JSON.parse(refreshedUserData));
                                    setIsAuthenticated(true);
                                }
                            })
                            .catch(() => {
                                logout();
                            })
                            .finally(() => {
                                setLoading(false);
                            });
                    } else {
                        setUser(JSON.parse(userData));
                        setIsAuthenticated(true);
                        setLoading(false);
                    }
                } catch (error) {
                    console.error('Error parsing user data:', error);
                    logout();
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        };

        checkAuth();
    }, []);

    const login = async (username, password) => {
        try {
            const response = await axios.post(
                getApiUrl('api/public/customer/login'),
                {
                    username,
                    password
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                }
            );

            // Handle both response structures:
            // 1. Wrapped: { success: true, data: { access_token, refresh_token, ... } }
            // 2. Direct: { access_token, refresh_token, ... }
            let tokenData = null;
            
            if (response.data.success && response.data.data) {
                // Wrapped response structure
                tokenData = response.data.data;
            } else if (response.data.access_token) {
                // Direct response structure
                tokenData = response.data;
            } else {
                return {
                    success: false,
                    message: response.data.message || 'Login failed. Invalid response format.'
                };
            }

            const { access_token, refresh_token, token_type, expires_in } = tokenData;
            
            // Validate access_token exists
            if (!access_token) {
                return {
                    success: false,
                    message: 'Login failed. Access token not received.'
                };
            }
            
            // Handle refresh_token - it might be an empty object or string
            let refreshTokenValue = null;
            if (refresh_token) {
                if (typeof refresh_token === 'string') {
                    refreshTokenValue = refresh_token;
                } else if (typeof refresh_token === 'object' && Object.keys(refresh_token).length > 0) {
                    // If it's an object with properties, try to extract a token
                    refreshTokenValue = refresh_token.token || refresh_token.refresh_token || null;
                }
            }
            
            // Decode JWT token to extract user information
            const decodedToken = decodeJWT(access_token);
            
            if (!decodedToken) {
                return {
                    success: false,
                    message: 'Failed to decode authentication token.'
                };
            }

            // Extract user data from token
            const userData = {
                id: decodedToken.customer_id || decodedToken.sub,
                name: decodedToken.name || username,
                username: decodedToken.username || username,
                email: decodedToken.email || '',
                mobile_number: decodedToken.mobile_number || '',
                state: decodedToken.state || ''
            };

            // Store tokens and user data
            localStorage.setItem('customer_access_token', access_token);
            if (refreshTokenValue) {
                localStorage.setItem('customer_refresh_token', refreshTokenValue);
            }
            if (token_type) {
                localStorage.setItem('customer_token_type', token_type);
            }
            if (expires_in) {
                localStorage.setItem('customer_token_expires_in', expires_in.toString());
            } else {
                // Calculate expires_in from token expiration if not provided
                const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
                const currentTime = Date.now();
                const expiresInSeconds = Math.floor((expirationTime - currentTime) / 1000);
                if (expiresInSeconds > 0) {
                    localStorage.setItem('customer_token_expires_in', expiresInSeconds.toString());
                }
            }
            localStorage.setItem('customer_user', JSON.stringify(userData));
            
            setUser(userData);
            setIsAuthenticated(true);
            
            return {
                success: true,
                data: userData
            };
        } catch (error) {
            // Handle different error types
            if (error.response) {
                const { status, data } = error.response;
                
                // Validation errors (422)
                if (status === 422 && data.errors) {
                    const errorMessages = Object.values(data.errors)
                        .flat()
                        .join(', ');
                    return {
                        success: false,
                        message: errorMessages || data.message || 'Validation error',
                        errors: data.errors
                    };
                }
                
                // Invalid credentials (401)
                if (status === 401) {
                    return {
                        success: false,
                        message: data.message || 'Invalid username or password.'
                    };
                }
                
                // Other errors
                return {
                    success: false,
                    message: data.message || 'Login failed. Please try again.'
                };
            }
            
            // Network or other errors
            return {
                success: false,
                message: 'Network error. Please check your connection and try again.'
            };
        }
    };

    // Logout function with API integration
    const logout = async () => {
        const accessToken = localStorage.getItem('customer_access_token');
        
        // Clear tokens immediately (optimistic logout)
        localStorage.removeItem('customer_user');
        localStorage.removeItem('customer_access_token');
        localStorage.removeItem('customer_refresh_token');
        localStorage.removeItem('customer_token_type');
        localStorage.removeItem('customer_token_expires_in');
        
        // Clear state immediately
        setUser(null);
        setIsAuthenticated(false);
        
        // Try to invalidate token on server (best effort)
        if (accessToken) {
            try {
                await customerApi.post(
                    getApiUrl('api/customer/logout'),
                    {},
                    {
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        }
                    }
                );
                // Logout successful on server
            } catch (error) {
                // Network error or server unavailable - tokens already cleared locally
                console.warn('Server logout failed, but tokens cleared locally:', error);
            }
        }
    };

    // Periodic token refresh check (every 30 minutes)
    useEffect(() => {
        if (!isAuthenticated) return;

        const interval = setInterval(() => {
            if (isTokenExpired()) {
                refreshToken().catch((error) => {
                    console.error('Periodic token refresh failed:', error);
                });
            }
        }, 30 * 60 * 1000); // Check every 30 minutes

        return () => clearInterval(interval);
    }, [isAuthenticated]);

    const value = {
        user,
        isAuthenticated,
        loading,
        login,
        logout,
        refreshToken
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Export the customer API instance for use in other components
export { customerApi };

